import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAnalyticsStore } from '~contexts/mobx';
import {
    analyticsToday,
    campaignRange,
    CampaignWindow,
} from '~containers/analytics/kpis/periods';
import type { KpiCampaignContext } from '~containers/analytics/kpis/types';
import type { ServedCampaign } from '~stores/analytics';

/** Which game's editions a page reads, and how it names them to a reader. */
export interface ServedEditionOptions {
    gameKind: NonNullable<ServedCampaign['gameKind']>;
    /** The edition read when the reader picked none. */
    defaultEditionId: string;
    /** The game's name in the two unavailable messages. */
    gameLabel: string;
}

type EditionsState =
    | { status: 'loading' }
    | { status: 'ready'; editions: ServedCampaign[] }
    | { status: 'failed' };

export interface ServedEdition {
    editions: ServedCampaign[];
    edition: ServedCampaign | undefined;
    select: (id: string) => void;
    /** For the period capsule, which takes the edition's own bounds. */
    campaign: CampaignWindow | undefined;
    /** For the cards, which take the resolved window or the reason there is
     * none. */
    context: KpiCampaignContext;
}

/**
 * The editions of one game the API serves, the one the page reads, and its
 * window. Shared by the NHL Coins and 31DOC dashboards.
 */
export const useServedEdition = ({
    gameKind,
    defaultEditionId,
    gameLabel,
}: ServedEditionOptions): ServedEdition => {
    const analyticsStore = useAnalyticsStore();
    const [state, setState] = useState<EditionsState>({ status: 'loading' });
    const [selectedId, setSelectedId] = useState<string>();

    useEffect(() => {
        let cancelled = false;
        analyticsStore
            .fetchServedCampaigns()
            .then((campaigns) => {
                if (cancelled) return;
                setState(
                    campaigns === null
                        ? { status: 'failed' }
                        : {
                              status: 'ready',
                              editions: campaigns.filter(
                                  (campaign) =>
                                      campaign.kind === 'edition' &&
                                      campaign.gameKind === gameKind
                              ),
                          }
                );
            })
            .catch(() => {
                if (!cancelled) setState({ status: 'failed' });
            });
        return () => {
            cancelled = true;
        };
    }, [analyticsStore, gameKind]);

    const editions = state.status === 'ready' ? state.editions : [];
    const edition =
        editions.find((candidate) => candidate.id === selectedId) ??
        editions.find((candidate) => candidate.id === defaultEditionId) ??
        editions[0];

    const select = useCallback((id: string) => setSelectedId(id), []);

    const { campaign, context } = useMemo<
        Pick<ServedEdition, 'campaign' | 'context'>
    >(() => {
        if (state.status === 'loading') {
            return { campaign: undefined, context: { status: 'loading' } };
        }
        if (!edition) {
            return {
                campaign: undefined,
                context: {
                    status: 'unavailable',
                    reason:
                        state.status === 'failed'
                            ? `The ${gameLabel} editions could not be loaded, so their figures cannot be framed.`
                            : `No ${gameLabel} edition is served in this environment.`,
                },
            };
        }
        const span: CampaignWindow = {
            startDate: edition.startDate,
            ...(edition.endDate && { endDate: edition.endDate }),
        };
        const today = analyticsToday();
        return {
            campaign: span,
            context: {
                status: 'ready',
                runsToday: !span.endDate || span.endDate >= today,
                toDate: {
                    ...campaignRange(span, today),
                    preset: { period: 'campaign', campaignId: edition.id },
                },
            },
        };
    }, [state.status, edition, gameLabel]);

    return { editions, edition, select, campaign, context };
};
